// What the controls do, in one place, so the buttons, the stage and the keys all behave the same.

import { engine, type ClaveSetting, type CueMode, type RestartReason } from '../engine/engine.js';
import { DEFAULT_LAB } from '../engine/lab.js';
import type { BellVoice } from '@salsacount/core';
import { loadBell, saveBell } from './lab-bell.js';
import { calibrationTap } from './calibrate-run.js';
import { keepSong } from './keep.js';
import { finishFirstRun } from './firstrun.js';
import { announce, closeSheet, enterPlayer, hideCountNote, hideRemovedNote, openSheet, showRemovedNote, showToast, ui } from './ui.js';

/**
 * Open a song file: show the player at once (it shows the decoding), then play so the dancer can tap. A copy is kept on this device
 * (unless the dancer turned that off, or the file came from the kept copy already).
 */
export async function openSong(file: File, opts: { fromCopy?: boolean } = {}): Promise<void> {
  void engine.ctx.resume(); // inside the gesture, which iOS needs
  finishFirstRun();
  enterPlayer();
  await engine.openFile(file);
  const s = engine.getState();
  if (s.status === 'error') return;
  if (!opts.fromCopy && ui.get().keep && s.track) void keepSong(file, s.track);
  await engine.play();
}

/** The Rhythm Lab: one tap on Home and the count is running. */
export async function openLab(): Promise<void> {
  void engine.ctx.resume();
  finishFirstRun();
  enterPlayer();
  await engine.openLab({ ...(engine.getState().lab ?? DEFAULT_LAB), bell: loadBell() });
  await engine.play();
}

/** The lab's bell voice: remembered on this device, and swapped in under the running count (no restart). */
export function setBell(voice: BellVoice): void {
  saveBell(voice);
  void engine.setLab({ bell: voice });
}

/** The stage was tapped (or T, or 1). */
export function tapStage(): void {
  if (calibrationTap()) return;
  const s = engine.getState();
  if (s.source === 'lab') {
    void engine.toggle(); // the lab's count is exact: nothing to tap, so the stage starts and stops the band
    return;
  }
  if (s.source !== 'file' || s.status === 'decoding' || s.status === 'error') return;
  if (s.replay) return; // a replay is playing: the stage ignores taps until it ends
  if (!s.playing) {
    if (s.status === 'needs-taps' || s.truthMode) {
      void engine.play(); // a tap on a stopped song has no time in it: start the song first
      announce('Playing. Tap when you feel 1.');
    } else {
      showToast('Press play, then tap on 1'); // a paused song has no timing: the count is left alone
    }
    return;
  }
  const was = s.auto.awaiting;
  const kind = engine.tap();
  if (kind === 'reanchor') {
    showToast(was ? 'Count set by you' : '1 moved here', true);
    if (was) announce('Count set by you');
  }
}

export type Fix = 'half' | 'earlier' | 'later' | 'flip';

const FIX_WORDS: Record<Fix, string> = {
  half: 'Count moved half a beat',
  earlier: '1 moved one beat earlier',
  later: '1 moved one beat later',
  flip: '1 and 5 swapped',
};

export function canFix(): boolean {
  const s = engine.getState();
  return s.source === 'file' && (s.taps > 0 || s.auto.origin !== 'none') && !s.truthMode && (s.status === 'provisional' || s.status === 'locked');
}

export function fix(kind: Fix): void {
  if (!canFix()) return;
  if (kind === 'half') engine.half();
  else if (kind === 'flip') engine.flip();
  else engine.shift(kind === 'earlier' ? -1 : 1);
  showToast(FIX_WORDS[kind], true);
}

// ── the automatic count's question (DESIGN §11, §11.1): Yes · 1↔5 · ½ beat · Too fast · Too slow ──

export type Answer = 'yes' | 'flip' | 'half' | 'halve' | 'double';

const ANSWER_WORDS: Record<Answer, string> = {
  yes: 'Count confirmed',
  flip: '1 and 5 swapped',
  half: 'Count moved half a beat',
  halve: 'Counting half as fast',
  double: 'Counting twice as fast',
};

/** The line over the confirm row: "Found automatically. Is 1 right?" when "1" is likely or sure, else the honest one (DESIGN §11.1). */
export function autoLine(oneSure: boolean): string {
  return oneSure ? 'Found automatically. Is 1 right?' : 'Found the beat. 1 may be four counts off: check it.';
}

/** The two sentences behind "How do I tell?" (DESIGN §11.1). */
export const HOW_TO_TELL = ['1 is where phrases start: a new line of singing, a horn entry, a change of section.', 'If those keep landing on 5, press 1↔5.'] as const;

export function canAnswer(): boolean {
  const s = engine.getState();
  return s.source === 'file' && !s.truthMode && s.auto.awaiting && (s.status === 'provisional' || s.status === 'locked');
}

/** One answer to the proposal: an event like every other (saved, undoable, with Undo in the message). */
export function answer(a: Answer): void {
  if (!canAnswer()) return;
  const done = a === 'yes' ? engine.confirm() : a === 'flip' ? engine.flipWhole() : a === 'half' ? (engine.half(), true) : engine.setLevel(a);
  if (done) showToast(ANSWER_WORDS[a], true);
}

export function setClave(setting: ClaveSetting): void {
  const s = engine.getState();
  if (s.source === 'lab') {
    if (setting !== 'unknown') void engine.setLab({ clave: setting });
    return;
  }
  if (!canFix() || setting === s.clave.setting) return;
  engine.setClave(setting);
  showToast(setting === 'unknown' ? 'Clave cleared' : `Clave set to ${setting}`, true);
}

export function undo(): void {
  const s = engine.getState();
  if (!s.canUndo || s.truthMode) return;
  engine.undo();
  showToast('Undone');
}

/** The action on a failed analysis or player: try again (the taps are kept). */
export function tryAgain(): Promise<void> {
  return engine.tryAgain();
}

/** Start over: clear the count and tap again. Undo brings it back. */
export function startOver(): void {
  if (!engine.startOver()) return;
  showToast('Started over. Tap on the next 1.', true);
}

/** One 8-count back or forward: to the 1 of the previous or next phrase (ten seconds with no count). */
export function seekPhrase(direction: -1 | 1): void {
  const starts = engine.phraseStarts();
  const now = engine.frame().song;
  if (starts.length === 0) {
    engine.seek(now + direction * 10);
    return;
  }
  let target: number | undefined;
  if (direction === 1) target = starts.find((t) => t > now + 0.05);
  else for (const t of starts) if (t < now - 0.6) target = t; // a moment into a phrase, back goes to its own 1 first
  engine.seek(target ?? (direction === 1 ? now : 0));
}

const CUE_ORDER: CueMode[] = ['off', 'steps', 'oneFive', 'all'];

export const CUE_NAMES: Record<CueMode, string> = { off: 'Off', steps: 'Steps', oneFive: '1 and 5', all: 'All' };

export function cycleCues(): void {
  const next = CUE_ORDER[(CUE_ORDER.indexOf(engine.getState().cueMode) + 1) % CUE_ORDER.length]!;
  engine.setCueMode(next);
  showToast(`Cues: ${CUE_NAMES[next]}`);
}

export function toggleLoop(): void {
  const s = engine.getState();
  if (s.status === 'needs-taps' || s.status === 'idle' || s.status === 'decoding') return;
  engine.setLoop(!s.loop.on);
  showToast(s.loop.on ? 'Loop off' : `Looping ${s.loop.phrases === 1 ? 'this 8-count' : `${s.loop.phrases} 8-counts`}`);
}

export function formatTime(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Is a sheet open? Keys go to it, not to the stage. */
export function sheetOpen(): boolean {
  return ui.get().sheet !== null;
}

// ── taps: undo, review, replay (DESIGN §9) ──

/** Why a tap started the seed again (DESIGN §9): one sentence, shown on the stage until the next tap and spoken by the live region. */
export function tapNoteWords(why: RestartReason): string {
  return why === 'soon'
    ? 'Too soon for the next 1. Starting again from this tap.'
    : 'That did not fit a count of eight. Starting again from this tap.';
}

/** Words for the taps bar: "1 tap", "3 taps". */
export function tapWords(n: number): string {
  return `${n} ${n === 1 ? 'tap' : 'taps'}`;
}

/** Undo tap (the button, and U): remove the most recent tap, whatever was corrected after it. The bar then offers Put back. */
export function undoTap(): void {
  const s = engine.getState();
  if (s.source !== 'file' || !s.canUndoTap) return;
  if (!engine.undoTap()) return;
  showRemovedNote();
  announce('Tap removed. Put back is available.');
}

/** Remove one named tap, or the automatic proposal (the Review sheet's Remove). */
export function removeTap(id: string): void {
  if (!engine.dropTap(id)) return;
  if (id.startsWith('auto@')) { announce('Automatic count removed. Tap when you feel 1.'); return; }
  showRemovedNote();
  announce('Tap removed');
}

/** Put a removed tap back: the latest one, or the one named (the Review sheet's Keep). */
export function putBackTap(id?: string): void {
  if (!engine.putBack(id)) return;
  hideRemovedNote();
  announce(id?.startsWith('auto@') ? 'Automatic count put back' : 'Tap put back');
}

/** Review taps (the button, and R). */
export function reviewTaps(): void {
  const s = engine.getState();
  if (s.source !== 'file' || s.truthMode || s.tapRows.length === 0) return;
  openSheet('review');
}

/** Replay from the Review sheet: close it so the stage shows the replay, and open it again when the replay ends. */
export function replayFromSheet(id: string | null): void {
  closeSheet();
  ui.patch({ reopenReview: true });
  void (id === null ? engine.replayAll() : engine.replayTap(id)).then((started) => {
    if (!started) { ui.patch({ reopenReview: false }); openSheet('review'); } // nothing to replay: stay where the dancer was
  });
}

/** Replay from "Count set from 2 taps · Replay": every tap, in order. */
export function replayAllTaps(): void {
  hideCountNote();
  void engine.replayAll();
}

export function stopReplay(): void {
  engine.stopReplay();
}
